import type { AppDatabase } from './store';
import type { Game, GameResult, Id, ScheduleSlot, Team, Tournament } from '../models';
import { missingPublishFields, setupStatus } from './tournamentService';
import { detectCrewConflicts } from './operationsService';
import { paths, tournamentSlug } from '../lib/paths';

export interface AdminDashboardLiveGame {
  game: Game;
  tournament?: Tournament;
  home?: Team;
  away?: Team;
  slot?: ScheduleSlot;
  result?: GameResult;
  href: string;
}

export interface AdminDashboardTodayGame extends AdminDashboardLiveGame {
  startsAt: string;
}

export interface AdminDashboardTask {
  id: string;
  label: string;
  detail: string;
  tone: 'warn' | 'info' | 'ok';
  href: string;
  actionLabel?: string;
  priority?: number;
}

export interface AdminDashboardAction {
  label: string;
  href: string;
}

export interface AdminDashboardActionCenter {
  message: string;
  primaryAction: AdminDashboardAction;
  secondaryActions: AdminDashboardAction[];
  allEventsAction: AdminDashboardAction;
  showNewEventMenu: boolean;
}

export interface AdminDashboardState {
  liveEvents: Tournament[];
  upcomingEvents: Tournament[];
  draftEvents: Tournament[];
  completedEvents: Tournament[];
  currentEvents: Tournament[];
  liveGames: AdminDashboardLiveGame[];
  todayGames: AdminDashboardTodayGame[];
  needsAction: AdminDashboardTask[];
  nextBestAction: AdminDashboardTask;
  actionCenter: AdminDashboardActionCenter;
}

export function buildAdminDashboardState(db: AppDatabase): AdminDashboardState {
  const tournaments = [...db.tournaments].sort((a, b) => b.startsAt.localeCompare(a.startsAt));
  const visibleTournamentIds = new Set(tournaments.filter((t) => t.status !== 'archived').map((t) => t.id));
  const liveEvents = tournaments.filter((t) => t.status === 'in_progress' || t.status === 'paused');
  const upcomingEvents = tournaments.filter((t) => t.status === 'scheduled');
  const draftEvents = tournaments.filter((t) => t.status === 'draft' || t.status === 'setup');
  const completedEvents = tournaments.filter((t) => t.status === 'completed');

  const tournamentById = new Map(db.tournaments.map((t) => [t.id, t]));
  const teamById = new Map(db.teams.map((t) => [t.id, t]));
  const slotById = new Map(db.scheduleSlots.map((s) => [s.id, s]));
  const resultByGameId = new Map(db.gameResults.map((r) => [r.gameId, r]));

  const liveGames = db.games
    .filter((game) => game.status === 'in_progress' && visibleTournamentIds.has(game.tournamentId))
    .map((game) => hydrateGame(game, tournamentById, teamById, slotById, resultByGameId));

  const todayKey = dateKey(new Date());
  const todayGames = db.games
    .filter((game) => visibleTournamentIds.has(game.tournamentId))
    .map((game) => hydrateGame(game, tournamentById, teamById, slotById, resultByGameId))
    .flatMap((item) => {
      if (!item.slot || dateKey(new Date(item.slot.startsAt)) !== todayKey) return [];
      return [{ ...item, startsAt: item.slot.startsAt }];
    })
    .sort((a, b) => a.startsAt.localeCompare(b.startsAt));

  const needsAction: AdminDashboardTask[] = [];

  for (const tournament of draftEvents) {
    const status = setupStatus(tournament);
    const href = paths.adminCompetition(tournament.format, tournamentSlug(tournament));
    if (status.missing.length === 0) {
      needsAction.push({
        id: `publish-${tournament.id}`,
        label: `Publish ${tournament.name}`,
        detail: 'Setup is complete and ready for players.',
        tone: 'ok',
        href,
        actionLabel: 'Publish event',
        priority: 25,
      });
    } else {
      needsAction.push({
        id: `setup-${tournament.id}`,
        label: `Finish setup: ${tournament.name}`,
        detail: `Missing ${missingPublishFields(tournament).join(', ')}.`,
        tone: 'warn',
        href,
        actionLabel: 'Continue setup',
        priority: 10,
      });
    }
  }

  for (const tournament of tournaments.filter((t) => t.status !== 'completed' && t.status !== 'archived')) {
    const tournamentGames = db.games.filter((g) => g.tournamentId === tournament.id && g.status !== 'final');
    const missingScorekeeper = tournamentGames.filter(
      (game) => !db.gameAssignments.some((assignment) => assignment.gameId === game.id && assignment.position === 'scorekeeper'),
    ).length;
    if (missingScorekeeper > 0) {
      needsAction.push({
        id: `crew-${tournament.id}`,
        label: `${missingScorekeeper} game${missingScorekeeper === 1 ? '' : 's'} need scorekeepers`,
        detail: tournament.name,
        tone: 'warn',
        href: paths.adminCompetitionTab(tournament.format, tournamentSlug(tournament), 'operations'),
        actionLabel: 'Assign crew',
        priority: 20,
      });
    }

    const conflicts = detectCrewConflicts(tournament.id);
    if (conflicts.length > 0) {
      needsAction.push({
        id: `conflict-${tournament.id}`,
        label: `${conflicts.length} crew conflict${conflicts.length === 1 ? '' : 's'}`,
        detail: tournament.name,
        tone: 'warn',
        href: paths.adminCompetitionTab(tournament.format, tournamentSlug(tournament), 'operations'),
        actionLabel: 'Review conflicts',
        priority: 15,
      });
    }
  }

  const pendingRegistrations = db.registrations.filter(
    (registration) =>
      registration.status === 'pending_review' ||
      registration.status === 'pending_payment' ||
      registration.status === 'pending_waiver',
  ).filter((registration) => visibleTournamentIds.has(registration.tournamentId)).length;
  if (pendingRegistrations > 0) {
    needsAction.push({
      id: 'pending-registrations',
      label: `${pendingRegistrations} registration${pendingRegistrations === 1 ? '' : 's'} need review`,
      detail: 'Open the related season or tournament registration settings.',
      tone: 'warn',
      href: paths.adminSeasons,
      actionLabel: 'Open seasons',
      priority: 18,
    });
  }

  const finalizableGames = db.games.filter((game) => game.status === 'ready' && visibleTournamentIds.has(game.tournamentId)).length;
  if (finalizableGames > 0) {
    needsAction.push({
      id: 'ready-games',
      label: `${finalizableGames} game${finalizableGames === 1 ? '' : 's'} ready to score`,
      detail: 'Open scoring from the event workspace.',
      tone: 'info',
      href: paths.adminTournaments,
      actionLabel: 'Open scoring',
      priority: 30,
    });
  }

  const failedDeliveries = db.communicationDeliveries.filter((delivery) => delivery.status === 'failed').length;
  if (failedDeliveries > 0) {
    needsAction.push({
      id: 'failed-deliveries',
      label: `${failedDeliveries} communication deliver${failedDeliveries === 1 ? 'y' : 'ies'} failed`,
      detail: 'Review Communication Hub delivery audit.',
      tone: 'warn',
      href: paths.adminComms,
      actionLabel: 'Open comms',
      priority: 12,
    });
  }

  const queuedDeliveries = db.communicationDeliveries.filter((delivery) => delivery.status === 'queued').length;
  if (queuedDeliveries > 0) {
    needsAction.push({
      id: 'queued-deliveries',
      label: `${queuedDeliveries} communication deliver${queuedDeliveries === 1 ? 'y is' : 'ies are'} queued`,
      detail: 'Check whether email delivery has completed.',
      tone: 'info',
      href: paths.adminComms,
      actionLabel: 'Open sent audit',
      priority: 45,
    });
  }

  const paymentIssues = db.paymentIntents.filter((payment) =>
    payment.status === 'failed' || payment.status === 'requires_payment_method',
  ).length;
  if (paymentIssues > 0) {
    needsAction.push({
      id: 'payment-issues',
      label: `${paymentIssues} payment issue${paymentIssues === 1 ? '' : 's'} need review`,
      detail: 'Open Financials to review failed or incomplete payment records.',
      tone: 'warn',
      href: paths.adminBilling,
      actionLabel: 'Open financials',
      priority: 16,
    });
  }

  needsAction.sort(compareTasks);

  const currentEvents = [...liveEvents, ...upcomingEvents, ...draftEvents, ...completedEvents]
    .filter((event, index, list) => list.findIndex((item) => item.id === event.id) === index)
    .slice(0, 6);

  const nextBestAction = needsAction[0] ?? fallbackNextAction({
    tournaments,
    liveEvents,
    upcomingEvents,
    draftEvents,
    todayGames,
  });
  const actionCenter = buildActionCenter({
    nextBestAction,
    tournaments,
    liveGames,
    needsAction,
    currentEvents,
  });

  return {
    liveEvents,
    upcomingEvents,
    draftEvents,
    completedEvents,
    currentEvents,
    liveGames,
    todayGames,
    needsAction,
    nextBestAction,
    actionCenter,
  };
}

function buildActionCenter(input: {
  nextBestAction: AdminDashboardTask;
  tournaments: Tournament[];
  liveGames: AdminDashboardLiveGame[];
  needsAction: AdminDashboardTask[];
  currentEvents: Tournament[];
}): AdminDashboardActionCenter {
  const hasEvents = input.tournaments.some((event) => event.status !== 'archived');
  const isCreateFirstSeason = input.nextBestAction.id === 'create-first-season';
  const message = input.liveGames.length > 0
    ? `${input.liveGames.length} live game${input.liveGames.length === 1 ? '' : 's'} need attention.`
    : input.needsAction.length > 0
      ? `${input.needsAction.length} item${input.needsAction.length === 1 ? '' : 's'} need attention.`
      : input.currentEvents[0]
        ? `Next focus: ${input.currentEvents[0].name}.`
        : 'Create your first season or tournament to start running the league.';

  return {
    message,
    primaryAction: isCreateFirstSeason
      ? { label: 'New season', href: paths.adminSeasonNew }
      : { label: input.nextBestAction.actionLabel ?? 'Open next', href: input.nextBestAction.href },
    secondaryActions: isCreateFirstSeason
      ? [{ label: 'New tournament', href: paths.adminTournamentNew }]
      : hasEvents
        ? [
            { label: 'Season', href: paths.adminSeasonNew },
            { label: 'Tournament', href: paths.adminTournamentNew },
          ]
        : [{ label: 'New tournament', href: paths.adminTournamentNew }],
    allEventsAction: { label: 'All events', href: paths.adminTournaments },
    showNewEventMenu: !isCreateFirstSeason && hasEvents,
  };
}

function compareTasks(a: AdminDashboardTask, b: AdminDashboardTask): number {
  const priority = (a.priority ?? 50) - (b.priority ?? 50);
  if (priority !== 0) return priority;
  const toneRank = { warn: 0, info: 1, ok: 2 };
  return toneRank[a.tone] - toneRank[b.tone];
}

function fallbackNextAction(input: {
  tournaments: Tournament[];
  liveEvents: Tournament[];
  upcomingEvents: Tournament[];
  draftEvents: Tournament[];
  todayGames: AdminDashboardTodayGame[];
}): AdminDashboardTask {
  if (input.tournaments.length === 0) {
    return {
      id: 'create-first-season',
      label: 'Create your first season',
      detail: 'Start with a season or tournament to begin setup.',
      tone: 'info',
      href: paths.adminSeasonNew,
      actionLabel: 'New season',
    };
  }
  if (input.todayGames.length > 0) {
    const first = input.todayGames[0];
    return {
      id: 'open-today-games',
      label: `${input.todayGames.length} game${input.todayGames.length === 1 ? '' : 's'} today`,
      detail: "Open today's scoring queue.",
      tone: 'info',
      href: first.href,
      actionLabel: 'Open scoring',
    };
  }
  const focus = input.liveEvents[0] ?? input.upcomingEvents[0] ?? input.draftEvents[0];
  if (focus) {
    return {
      id: `open-${focus.id}`,
      label: `Open ${focus.name}`,
      detail: 'Continue from the event workspace.',
      tone: 'info',
      href: paths.adminCompetition(focus.format, tournamentSlug(focus)),
      actionLabel: 'Open event',
    };
  }
  return {
    id: 'review-events',
    label: 'Review league events',
    detail: 'Open seasons and tournaments from the event list.',
    tone: 'info',
    href: paths.adminTournaments,
    actionLabel: 'All events',
  };
}

function hydrateGame(
  game: Game,
  tournamentById: Map<Id, Tournament>,
  teamById: Map<Id, Team>,
  slotById: Map<Id, ScheduleSlot>,
  resultByGameId: Map<Id, GameResult>,
): AdminDashboardLiveGame {
  const tournament = tournamentById.get(game.tournamentId);
  return {
    game,
    tournament,
    home: teamById.get(game.homeTeamId),
    away: teamById.get(game.awayTeamId),
    slot: game.scheduleSlotId ? slotById.get(game.scheduleSlotId) : undefined,
    result: resultByGameId.get(game.id),
    href: tournament ? paths.adminCompetitionTab(tournament.format, tournamentSlug(tournament), 'scoring') : paths.adminTournaments,
  };
}

function dateKey(date: Date): string {
  return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`;
}
